/**
 * Zustand store for workspace Voices and session seats.
 */

import { create } from 'zustand';
import {
  addSessionVoiceSeat,
  getVoice,
  listCorpusDocuments,
  listSessionVoiceSeats,
  listSponsoredVoices,
  listVoiceMemberships,
  listWorkspaceVoices,
  removeSessionVoiceSeat,
  setSeatCountingPolicy,
  setVoiceMemberships,
  updateVoiceSponsor,
  upsertVoice,
  type AddSessionVoiceSeatInput,
  type UpsertVoiceInput,
} from './voiceService';
import type {
  SessionVoiceSeat,
  VoiceCorpusDocument,
  VoiceMembership,
  WorkspaceVoice,
} from './types';

interface VoicesState {
  voices: WorkspaceVoice[];
  seatsBySession: Record<string, SessionVoiceSeat[]>;
  corpusByVoice: Record<string, VoiceCorpusDocument[]>;
  membershipsByVoice: Record<string, VoiceMembership[]>;
  isLoading: boolean;
  error: string | null;

  fetchVoices: (workspaceId: string) => Promise<void>;
  fetchSponsored: (workspaceId: string, sponsorUserId: string) => Promise<WorkspaceVoice[]>;
  fetchVoiceDetail: (voiceId: string) => Promise<WorkspaceVoice | null>;
  saveVoice: (input: UpsertVoiceInput) => Promise<WorkspaceVoice | null>;
  reassignSponsor: (voiceId: string, sponsorUserId: string) => Promise<boolean>;
  saveMemberships: (voiceId: string, orgUnitIds: string[]) => Promise<boolean>;
  fetchCorpus: (voiceId: string) => Promise<void>;
  fetchSeats: (sessionId: string) => Promise<void>;
  addSeat: (input: AddSessionVoiceSeatInput) => Promise<SessionVoiceSeat | null>;
  removeSeat: (sessionId: string, seatId: string) => Promise<boolean>;
  /** Facilitator opt-in: advisory ↔ counted with attribution (PRD §4). */
  setSeatPolicy: (
    sessionId: string,
    seatId: string,
    policy: 'advisory' | 'counted',
    changedBy: string,
  ) => Promise<boolean>;
  getActiveSeats: (sessionId: string) => SessionVoiceSeat[];
}

export const useVoicesStore = create<VoicesState>()((set, get) => ({
  voices: [],
  seatsBySession: {},
  corpusByVoice: {},
  membershipsByVoice: {},
  isLoading: false,
  error: null,

  fetchVoices: async (workspaceId) => {
    set({ isLoading: true, error: null });
    const { data, error } = await listWorkspaceVoices(workspaceId);
    set({ voices: data, isLoading: false, error });
  },

  fetchSponsored: async (workspaceId, sponsorUserId) => {
    const { data } = await listSponsoredVoices(workspaceId, sponsorUserId);
    return data;
  },

  fetchVoiceDetail: async (voiceId) => {
    const { data, error } = await getVoice(voiceId);
    if (error) set({ error });
    if (data) {
      set((s) => ({
        voices: s.voices.some((v) => v.id === data.id)
          ? s.voices.map((v) => (v.id === data.id ? data : v))
          : [...s.voices, data],
      }));
      const mem = await listVoiceMemberships(voiceId);
      if (!mem.error) {
        set((s) => ({
          membershipsByVoice: { ...s.membershipsByVoice, [voiceId]: mem.data },
        }));
      }
    }
    return data;
  },

  saveVoice: async (input) => {
    const { data, error } = await upsertVoice(input);
    if (error) {
      set({ error });
      return null;
    }
    if (data) {
      set((s) => ({
        voices: s.voices.some((v) => v.id === data.id)
          ? s.voices.map((v) => (v.id === data.id ? data : v))
          : [...s.voices, data],
        error: null,
      }));
    }
    return data;
  },

  reassignSponsor: async (voiceId, sponsorUserId) => {
    const { error } = await updateVoiceSponsor(voiceId, sponsorUserId);
    if (error) {
      set({ error });
      return false;
    }
    await get().fetchVoiceDetail(voiceId);
    return true;
  },

  saveMemberships: async (voiceId, orgUnitIds) => {
    const { error } = await setVoiceMemberships(voiceId, orgUnitIds);
    if (error) {
      set({ error });
      return false;
    }
    set((s) => ({
      membershipsByVoice: {
        ...s.membershipsByVoice,
        [voiceId]: orgUnitIds.map((orgUnitId) => ({
          id: `local-${orgUnitId}`,
          voiceId,
          orgUnitId,
          createdAt: new Date().toISOString(),
        })),
      },
    }));
    return true;
  },

  fetchCorpus: async (voiceId) => {
    const { data, error } = await listCorpusDocuments(voiceId);
    if (error) set({ error });
    set((s) => ({
      corpusByVoice: { ...s.corpusByVoice, [voiceId]: data },
    }));
  },

  fetchSeats: async (sessionId) => {
    const { data, error } = await listSessionVoiceSeats(sessionId);
    if (error) set({ error });
    set((s) => ({
      seatsBySession: { ...s.seatsBySession, [sessionId]: data },
    }));
  },

  addSeat: async (input) => {
    const { data, error } = await addSessionVoiceSeat(input);
    if (error) {
      set({ error });
      return null;
    }
    if (data) {
      set((s) => {
        const prev = s.seatsBySession[input.sessionId] ?? [];
        const without = prev.filter((x) => x.id !== data.id && x.voiceId !== data.voiceId);
        return {
          seatsBySession: {
            ...s.seatsBySession,
            [input.sessionId]: [...without, data],
          },
          error: null,
        };
      });
    }
    return data;
  },

  removeSeat: async (sessionId, seatId) => {
    const { error } = await removeSessionVoiceSeat(seatId);
    if (error) {
      set({ error });
      return false;
    }
    set((s) => ({
      seatsBySession: {
        ...s.seatsBySession,
        [sessionId]: (s.seatsBySession[sessionId] ?? []).map((x) =>
          x.id === seatId ? { ...x, status: 'removed' as const } : x,
        ),
      },
    }));
    return true;
  },

  setSeatPolicy: async (sessionId, seatId, policy, changedBy) => {
    const { data, error } = await setSeatCountingPolicy({ seatId, policy, changedBy });
    if (error || !data) {
      set({ error: error ?? 'Failed to update seat policy' });
      return false;
    }
    set((s) => ({
      seatsBySession: {
        ...s.seatsBySession,
        [sessionId]: (s.seatsBySession[sessionId] ?? []).map((x) =>
          x.id === seatId ? data : x,
        ),
      },
      error: null,
    }));
    return true;
  },

  getActiveSeats: (sessionId) =>
    (get().seatsBySession[sessionId] ?? []).filter((s) => s.status === 'active'),
}));
